import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ApartmentOutlined as FacilityIcon, LinkOutlined as AssignIcon } from '@mui/icons-material';
import type { UserSchema } from '../../types/auth.type';

function getUserId(user: UserSchema): string {
  return user.sub || user.id || '';
}

function getManagerName(user: UserSchema): string {
  const first = (user.data as { first_name?: string } | undefined)?.first_name?.trim();
  const last = (user.data as { last_name?: string } | undefined)?.last_name?.trim();
  const fullName = user.full_name?.trim() || [first, last].filter(Boolean).join(' ');
  return fullName || user.username || getUserId(user);
}

export interface AssignFacilityManagerDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  facilityName: string;
  availableManagers: UserSchema[];
  managerId: string;
  onManagerIdChange: (managerId: string) => void;
  onSubmit: () => void;
}

export default function AssignFacilityManagerDialog({
  open,
  onClose,
  loading,
  facilityName,
  availableManagers,
  managerId,
  onManagerIdChange,
  onSubmit,
}: AssignFacilityManagerDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ elevation: 0, sx: { border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2, bgcolor: alpha(theme.palette.primary.main, 0.06), borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, flexShrink: 0 }}>
            <FacilityIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {t('facilities.details.changeManager')}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={facilityName} sx={{ display: 'block', mt: 0.25 }}>
              {facilityName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Box component="form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }} noValidate>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <TextField
            fullWidth
            size="small"
            select
            label={t('facilities.details.selectManager')}
            value={managerId}
            onChange={(e) => onManagerIdChange(e.target.value)}
            disabled={availableManagers.length === 0}
            helperText={availableManagers.length === 0 ? t('facilities.details.noAvailableManagers') : undefined}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          >
            {availableManagers.length === 0 ? (
              <MenuItem disabled value="">{t('facilities.details.noAvailableManagers')}</MenuItem>
            ) : (
              availableManagers.map((manager) => {
                const id = getUserId(manager);
                return (
                  <MenuItem key={id} value={id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{getManagerName(manager)}</Typography>
                      {manager.username ? (
                        <Typography variant="caption" color="text.secondary" noWrap>{manager.username}</Typography>
                      ) : null}
                    </Box>
                  </MenuItem>
                );
              })
            )}
          </TextField>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 2, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('facilities.form.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!managerId || loading || availableManagers.length === 0}
            startIcon={<AssignIcon />}
            sx={{ textTransform: 'none' }}
          >
            {loading ? t('facilities.details.assigning') : t('facilities.details.assign')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
